import { useReducer, Dispatch, useEffect } from 'react';
import * as React from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { Listing as ListingResponse } from '@zoopla/listing-search-queries';
import { RawFilter } from '@zoopla/redux-types';
import { RawSortOrderOption } from '@zoopla/redux-types';
import { DfpTargeting, coerceDfpAdTargetingObject } from '@zoopla/adverts/dfp';
import { saveSearchAndAlertUrlFixer } from '@zoopla/listing-search-utils';

import { ActionAfterAuth } from '@zoopla/authentication/modal';
import {
    SearchPageType,
    ResultsItem,
    SearchType
} from './listingSearchContext.types';
import { SeoLinks } from './SeoLinks.types';

export type Favourites = {
    listings: string[];
};

export type EventNamesList =
    | 'Search'
    | 'Search Result Impression'
    | 'Viewed Page'
    | 'Lead Sent'
    | 'Search Result Click'
    | 'Click Save Search'
    | 'Click Save Search Submitted'
    | 'Click Email Property Alert'
    | 'Click Email Property Alert Submitted'
    | 'Click Call Agent'
    | 'Click Email Agent'
    | 'Click Save Listing'
    | 'Click Book Agent Valuation'
    | 'Development Lead'
    | 'Share Content'
    | 'Email Marketing Opt-In'
    | 'Registration'
    | 'Sign In'
    | 'Listing Lead Sent'
    | 'Phone Click To Reveal'
    | 'Save Listing'
    | 'Unsave Listing'
    | 'Click Email Alert'
    | 'Click Edit Search'
    | 'Click Enable Email Alert';

export type TaxonomyPage =
    | '/homepage/'
    | '/home-values/'
    | '/valuation/'
    | '/privacy/'
    | '/discover/'
    | '/property/details/'
    | '/for-sale/results/'
    | '/for-sale/details/'
    | '/for-sale/contact/'
    | '/to-rent/results/'
    | '/to-rent/details/'
    | '/to-rent/contact/'
    | '/new-homes/results/'
    | '/new-homes/details/'
    | '/new-homes/contact/'
    | '/signin/'
    | '/register/'
    | '/schemes/help-to-buy/'
    | '/schemes/help-to-buy/scotland/'
    | '/schemes/help-to-buy/wales/'
    | '/schemes/shared-ownership/'
    | '/my-home/select/';

export type TaxonomySection =
    | 'homepage'
    | 'home-values'
    | 'house-prices'
    | 'for-sale'
    | 'to-rent'
    | 'new-homes'
    | 'privacy'
    | 'discover'
    | 'signin'
    | 'register'
    | 'help-to-buy'
    | 'shared-ownership'
    | 'new homes/developers'
    | 'my-home';

export type AnalyticsTaxonomy = {
    url: string;
    areaName: string;
    activity: string;
    brand: string;
    countryCode: string;
    countyAreaName: string;
    currencyCode: string;
    listingsCategory: string;
    outcode: string;
    outcodes?: string[];
    page: TaxonomyPage;
    postalArea: string;
    priceMax?: string;
    priceMin?: string;
    radius: number;
    radiusAutoexpansion: string | null;
    regionName?: string;
    resultsSort: string;
    searchGuid: string;
    searchIdentifier: string;
    searchLocation: string;
    section: TaxonomySection;
    viewType: string;
    searchResultsCount: number;
    expandedResultsCount: number;
    totalResults: number;
    emailAllFormShown: boolean;
    emailAllTotalAgents: number;
    bedsMax: string | null;
    bedsMin: string | null;
    location: string | null;
    propertyType: string | null;
    geoIdentifier: string;
};

export type AnalyticsEcommerce = {
    currencyCode: string;
    impressions?: AnalyticsEcommerceImpression[];
};

export type AnalyticsEcommerceImpression = {
    id: number;
    list: string;
    position: number;
    variant: string;
};

export type ProductTrackingObject = {
    id: string;
    list: string;
    position: number;
    variant: string;
};

export type AdTargeting = {
    activity?: string | null;
    areaName?: string | null;
    brand?: string | null;
    brandPrimary?: string | null;
    countryCode?: string | null;
    currencyCode?: string | null;
    listingsCategory?: string | null;
    outcode?: string | null;
    outcodes?: string[] | null;
    page?: string | null;
    postalArea?: string | null;
    priceMax?: string | null;
    priceMin?: string | null;
    regionName?: string | null;
    resultsSort?: string | null;
    searchLocation?: string | null;
    searchResultsCount?: number | null;
    section?: string | null;
    totalResults?: number | null;
    url?: string | null;
    viewType?: string | null;
};

export type MetaInfo = {
    title: string;
    canonicalUri: string;
    description: string;
};

export type Pagination = {
    pageNumber: number;
    totalResults: number;
    pageNumberMax: number;
    totalResultsWasLimited: boolean;
};

export type Breadcrumb = {
    label: string | null;
    tracking?: string | null;
    uri?: string | null;
    i18nLabelKey?: string | null;
    noAppend?: number | null;
};
export type Links = {
    saveSearch: string | null;
    createAlert: string | null;
};

export type DiscoverMore = {
    housePricesUri: string | null;
    findAgentsUri: string | null;
};

export type SearchSource =
    | 'refine'
    | 'facets'
    | ''
    | 'home'
    | 'new-homes'
    | 'new-homes-dev';

export type SearchContextType = {
    dfpAdTargeting: DfpTargeting;
    seoAccordions: SeoLinks[];
    favourites: Favourites;
    links: Links;
    currentSearchSource: SearchSource;
    searchType: SearchPageType;
    searchCategory: SearchCategory;
    transactionType: SeoBlurb['transactionType'];
    pageTitle: string;
    showLocationValidation: boolean;
    userAlertId: string | null;
    savedSearchAndAlerts: SavedSearchAndAlerts | null;
    previousSavedSearchUri: string | null;
    isEditSavedSearchBannerVisible: boolean;
    isEditSavedSearchSuccess: boolean | null;
    actionAfterAuth: ActionAfterAuth | null;
};

export type Listings = {
    regular: ListingResponse[];
    extended: ListingResponse[];
    featured: ListingResponse[];
};

export type SearchCategory = 'commercial' | 'residential' | '';

export type TransactionType = 'sale' | 'rent';

export type SeoBlurb = {
    category: SearchCategory;
    transactionType: TransactionType;
};

export type SearchResults = {
    analyticsTaxonomy: AnalyticsTaxonomy;
    analyticsEcommerce: AnalyticsEcommerce;
    adTargeting?: AdTargeting;
    metaInfo: MetaInfo;
    pagination: Pagination;
    filters: {
        fields: RawFilter[];
    };
    links: Links;
    sortOrder: {
        currentSortOrder: string;
        options: RawSortOrderOption[];
    };
    listings: Listings;
    seoBlurb: SeoBlurb;
    title: string;
    userAlertId: string | null;
    savedSearchAndAlerts: SavedSearchAndAlerts | null;
    polyenc: string | null;
    polygon?: number[][][] | null;
};

export type ResultsList = {
    extended: ListingResponse[];
    main: ResultsItem[];
};

export type SearchResultsV2 = {
    analyticsTaxonomy: AnalyticsTaxonomy;
    analyticsEcommerce: AnalyticsEcommerce;
    adTargeting?: AdTargeting;
    metaInfo: MetaInfo;
    pagination: Pagination;
    filters: {
        fields: RawFilter[];
    };
    links: Links;
    sortOrder: {
        currentSortOrder: string;
        options: RawSortOrderOption[];
    };
    results: ResultsList;
    seoBlurb: SeoBlurb;
    title: string;
    userAlertId: string | null;
    savedSearchAndAlerts: SavedSearchAndAlerts | null;
    polyenc: string | null;
    polygon?: number[][][] | null;
};

export type SavedSearchAndAlerts = {
    uri: string;
    currentFrequency: RawSortOrderOption;
};

export type Redirect = {
    code: number;
    redirectUri: string;
};

export type ServerData = {
    breadcrumbs: Breadcrumb[];
    seoAccordions: SeoLinks[];
    discoverMore: DiscoverMore;
    searchResults: SearchResults;
    redirects: Redirect[];
};

// Temporary type for testing new graphql resolver - to be worked into ServerData once new endpoint is fully adopted.
export type ServerDataV2 = {
    breadcrumbs: Breadcrumb[];
    seoAccordions: SeoLinks[];
    discoverMore: DiscoverMore;
    searchResultsV2: SearchResultsV2;
    redirects: Redirect[];
};

export type defaultContext = {
    state: SearchContextType;
    dispatch: Dispatch<Action>;
};

export const defaultState: SearchContextType = {
    favourites: {
        listings: []
    },
    dfpAdTargeting: {
        activity: '',
        area_name: '',
        brand: '',
        brand_primary: '',
        country_code: '',
        currency_code: '',
        device: '',
        listings_category: '',
        outcode: '',
        outcodes: [''],
        os: '',
        page: '',
        platform: '',
        postal_area: '',
        price_max: '',
        price_min: '',
        region_name: '',
        results_sort: '',
        search_location: '',
        search_results_count: '0',
        section: '',
        total_results: '0',
        url: '',
        view_type: ''
    },
    seoAccordions: [],
    links: {
        saveSearch: '',
        createAlert: ''
    },
    currentSearchSource: '',
    searchType: SearchType.ForSale,
    searchCategory: '',
    transactionType: 'sale',
    pageTitle: '',
    showLocationValidation: false, // nothing is ever setting this
    userAlertId: null,
    savedSearchAndAlerts: null,
    previousSavedSearchUri: null,
    isEditSavedSearchBannerVisible: true,
    isEditSavedSearchSuccess: null,
    actionAfterAuth: null
};

export const generateInitialSearchState = (
    partial: Partial<SearchContextType> = {}
) => ({ ...defaultState, ...partial });

const defaultValue: defaultContext = {
    state: cloneDeep(defaultState),
    dispatch: () => ({})
};

export type Action =
    | { type: 'SET_DFP_AD_TARGETING'; payload: DfpTargeting }
    | { type: 'SET_SEO_LINKS'; payload: SeoLinks[] }
    | { type: 'SET_LINKS'; payload: Links }
    | {
          type: 'SET_LISTINGS_USER_FAVOURITES';
          payload: {
              favourites: Favourites;
          };
      }
    | { type: 'ADD_FAVOURITE_LISTING'; payload: string }
    | { type: 'REMOVE_FAVOURITE_LISTING'; payload: string }
    | { type: 'SET_SEARCH_SOURCE'; payload: SearchSource }
    | { type: 'SET_USER_ALERT_ID'; payload: string | null }
    | {
          type: 'SET_SAVED_SEARCH_AND_ALERTS';
          payload: SavedSearchAndAlerts | null;
      }
    | { type: 'SET_PREVIOUS_SAVED_SEARCH_URI'; payload: string | null }
    | { type: 'SET_ACTION_AFTER_AUTH'; payload: ActionAfterAuth | null }
    | { type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE'; payload: boolean }
    | { type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS'; payload: boolean | null }
    | { type: 'UPDATE_LAZY_INITIAL_STATE'; payload: SearchContextType };

export const searchReducer = (
    state: SearchContextType,
    action: Action
): SearchContextType => {
    switch (action.type) {
        case 'SET_DFP_AD_TARGETING':
            return { ...state, dfpAdTargeting: action.payload };
        case 'SET_SEO_LINKS':
            return { ...state, seoAccordions: action.payload };
        case 'SET_LINKS':
            return { ...state, links: action.payload };
        case 'SET_LISTINGS_USER_FAVOURITES':
            return {
                ...state,
                favourites: action.payload.favourites
            };
        case 'ADD_FAVOURITE_LISTING':
            return {
                ...state,
                favourites: {
                    /* Todo */
                    /* eslint-disable-next-line no-unsafe-optional-chaining */
                    listings: [...state.favourites?.listings].concat([
                        action.payload
                    ])
                }
            };
        case 'REMOVE_FAVOURITE_LISTING':
            return {
                ...state,
                favourites: {
                    /* Todo */
                    /* eslint-disable-next-line no-unsafe-optional-chaining */
                    listings: [...state.favourites?.listings].filter(
                        (favouriteId) => favouriteId !== action.payload
                    )
                }
            };
        case 'SET_SEARCH_SOURCE':
            return {
                ...state,
                currentSearchSource: action.payload
            };

        case 'SET_USER_ALERT_ID':
            return {
                ...state,
                userAlertId: action.payload
            };
        case 'SET_SAVED_SEARCH_AND_ALERTS':
            return {
                ...state,
                savedSearchAndAlerts: action.payload
                    ? {
                          uri: saveSearchAndAlertUrlFixer(
                              action.payload.uri
                          ) as string,
                          currentFrequency: action.payload.currentFrequency
                      }
                    : null
            };
        case 'SET_PREVIOUS_SAVED_SEARCH_URI':
            return {
                ...state,
                previousSavedSearchUri: action.payload
            };
        case 'SET_ACTION_AFTER_AUTH':
            return {
                ...state,
                actionAfterAuth: action.payload
            };
        case 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE':
            return {
                ...state,
                isEditSavedSearchBannerVisible: action.payload
            };
        case 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS':
            return {
                ...state,
                isEditSavedSearchSuccess: action.payload
            };
        case 'UPDATE_LAZY_INITIAL_STATE':
            return {
                ...state,
                dfpAdTargeting: action.payload.dfpAdTargeting,
                seoAccordions: action.payload.seoAccordions,
                links: action.payload.links, // only one not in v2 map (?)
                userAlertId: action.payload.userAlertId,
                savedSearchAndAlerts: action.payload.savedSearchAndAlerts,
                previousSavedSearchUri: action.payload.previousSavedSearchUri,
                isEditSavedSearchBannerVisible:
                    action.payload.isEditSavedSearchBannerVisible,
                isEditSavedSearchSuccess:
                    action.payload.isEditSavedSearchSuccess
            };
    }
};

export const SearchContext = React.createContext(defaultValue);
SearchContext.displayName = 'SearchContext';

export const mapDataToActions = (serverData: ServerData): Action[] => {
    const actions: Action[] = [];
    actions.push({
        type: 'SET_DFP_AD_TARGETING',
        payload: coerceDfpAdTargetingObject(
            serverData?.searchResults?.adTargeting
        )
    });
    actions.push({
        type: 'SET_SEO_LINKS',
        payload: serverData?.seoAccordions
    });
    actions.push({
        type: 'SET_LINKS',
        payload: serverData?.searchResults.links
    });
    actions.push({
        type: 'SET_USER_ALERT_ID',
        payload: serverData?.searchResults?.userAlertId
    });
    actions.push({
        type: 'SET_SAVED_SEARCH_AND_ALERTS',
        payload: serverData?.searchResults?.savedSearchAndAlerts
    });

    if (serverData?.searchResults?.userAlertId === null) {
        actions.push({
            type: 'SET_PREVIOUS_SAVED_SEARCH_URI',
            payload: null
        });
    }

    if (!serverData?.searchResults?.savedSearchAndAlerts) {
        actions.push({
            type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
            payload: true
        });

        actions.push({
            type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
            payload: null
        });
    }

    return actions;
};

export const mapDataToActionsV2 = (serverData: ServerDataV2): Action[] => {
    const actions: Action[] = [];
    actions.push(
        {
            type: 'SET_DFP_AD_TARGETING',
            payload: coerceDfpAdTargetingObject(
                serverData?.searchResultsV2?.adTargeting
            )
        },
        {
            type: 'SET_SEO_LINKS',
            payload: serverData?.seoAccordions
        },
        {
            type: 'SET_USER_ALERT_ID',
            payload: serverData?.searchResultsV2?.userAlertId
        },
        {
            type: 'SET_SAVED_SEARCH_AND_ALERTS',
            payload: serverData?.searchResultsV2?.savedSearchAndAlerts
        }
    );

    if (serverData?.searchResultsV2?.userAlertId === null) {
        actions.push({
            type: 'SET_PREVIOUS_SAVED_SEARCH_URI',
            payload: null
        });
    }

    if (!serverData?.searchResultsV2?.savedSearchAndAlerts) {
        actions.push(
            {
                type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
                payload: true
            },
            {
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: null
            }
        );
    }

    return actions;
};

type Props = {
    initialState?: SearchContextType;
};

export const SearchProvider: React.FC<React.PropsWithChildren<Props>> = ({
    children,
    initialState
}) => {
    const [state, dispatch] = useReducer(
        searchReducer,
        initialState || defaultState
    );

    useEffect(() => {
        if (initialState) {
            // When in CLIENT_SIDE_DATA_FETCH mode we receive new initialState
            // lazily. And therefore we cannot blindly use it as the new state
            // as it may be out of sync with mutations already made (i.e fav
            // listing data). This new state has already been formatted so we
            // just need to apply the data updates we care about
            dispatch({
                type: 'UPDATE_LAZY_INITIAL_STATE',
                payload: initialState
            });
        }
    }, [initialState]);

    return (
        <SearchContext.Provider value={{ state, dispatch }}>
            {children}
        </SearchContext.Provider>
    );
};
